import React, { useEffect, useState, useMemo } from "react";
import {
  Search,
  Calendar,
  X,
  RefreshCw,
  Clock,
  CheckCircle2,
  Package,
  Truck,
  XCircle,
  AlertCircle,
  Layers,
  Trash2,
} from "lucide-react";

const API_BASE_URL = "https://api.vanyacollectionsrajahmundry.com";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("all"); // 'all', 'today', 'yesterday', 'week', 'month', 'custom'
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Shipping / Courier modal
  const [shippingModal, setShippingModal] = useState({
    open: false,
    order: null,
    courier_name: "",
    tracking_number: "",
    tracking_url: "",
  });

  // Delete modal with confirming pattern alert
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    order: null,
    confirmInput: "",
    isDeleting: false,
    errorMessage: "",
  });

  // ============================
  // FETCH ORDERS
  // ============================
  const fetchOrders = async () => {
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/orders/all`);

      if (!res.ok) {
        throw new Error("Failed to fetch orders");
      }

      const data = await res.json();

      if (Array.isArray(data)) {
        setOrders(data);
      } else if (Array.isArray(data.orders)) {
        setOrders(data.orders);
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // ============================
  // ORDER CODE & DATE FORMATTING HELPERS
  // ============================
  const getOrderCode = (order) => {
    if (!order) return "";
    return order.order_number || (order.id < 10 ? `VAN-0${order.id}` : `VAN-${order.id}`);
  };

  // Converts date into DD-MM-YYYY format
  const formatDateDDMMYYYY = (dateVal) => {
    if (!dateVal) return "-";
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Converts date into DD-MM-YYYY at hh:mm AM/PM format
  const formatDateTimeDDMMYYYY = (dateVal) => {
    if (!dateVal) return "-";
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedHours = String(hours).padStart(2, "0");
    return `${day}-${month}-${year} at ${formattedHours}:${minutes} ${ampm}`;
  };

  // ============================
  // DATE PRESET HANDLER
  // ============================
  const handleDatePreset = (preset) => {
    setDatePreset(preset);
    const now = new Date();
    const toISODate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    if (preset === "all") {
      setStartDate("");
      setEndDate("");
    } else if (preset === "today") {
      const todayStr = toISODate(now);
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (preset === "yesterday") {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const yStr = toISODate(y);
      setStartDate(yStr);
      setEndDate(yStr);
    } else if (preset === "week") {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      setStartDate(toISODate(weekAgo));
      setEndDate(toISODate(now));
    } else if (preset === "month") {
      const monthAgo = new Date(now);
      monthAgo.setDate(monthAgo.getDate() - 30);
      setStartDate(toISODate(monthAgo));
      setEndDate(toISODate(now));
    }
  };

  // ============================
  // FILTERING LOGIC
  // ============================
  // 1. Filter by Date Range (all stats computed from this set!)
  const dateFilteredOrders = useMemo(() => {
    if (!startDate && !endDate) return orders;

    return orders.filter((ord) => {
      if (!ord.created_at) return false;
      const orderDate = new Date(ord.created_at);
      if (isNaN(orderDate.getTime())) return false;

      if (startDate) {
        const start = new Date(startDate + "T00:00:00");
        if (orderDate < start) return false;
      }
      if (endDate) {
        const end = new Date(endDate + "T23:59:59.999");
        if (orderDate > end) return false;
      }
      return true;
    });
  }, [orders, startDate, endDate]);

  // 2. Dynamic Stats (strictly based on dateFilteredOrders)
  const stats = useMemo(() => {
    const total = dateFilteredOrders.length;
    let confirmed = 0;
    let packed = 0;
    let shipped = 0;
    let delivered = 0;
    let cancelled = 0;
    let pending = 0;
    let totalRevenue = 0;

    dateFilteredOrders.forEach((o) => {
      const st = (o.order_status || "pending").toLowerCase();
      const amt = parseFloat(o.total_amount) || 0;
      totalRevenue += amt;

      if (st === "confirmed") confirmed++;
      else if (st === "packed") packed++;
      else if (st === "shipped") shipped++;
      else if (st === "delivered") delivered++;
      else if (st === "cancelled" || st === "payment_rejected") cancelled++;
      else pending++;
    });

    return {
      total,
      confirmed,
      packed,
      shipped,
      delivered,
      cancelled,
      pending,
      totalRevenue,
    };
  }, [dateFilteredOrders]);

  // 3. Search & Status Filtering (applied on dateFilteredOrders)
  const displayedOrders = useMemo(() => {
    let list = dateFilteredOrders;

    // Status filter
    if (statusFilter !== "all") {
      if (statusFilter === "pending") {
        list = list.filter(
          (o) =>
            !o.order_status ||
            o.order_status === "pending" ||
            o.order_status === "payment_pending"
        );
      } else if (statusFilter === "cancelled") {
        list = list.filter(
          (o) =>
            o.order_status === "cancelled" ||
            o.order_status === "payment_rejected"
        );
      } else {
        list = list.filter(
          (o) => (o.order_status || "").toLowerCase() === statusFilter.toLowerCase()
        );
      }
    }

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      const qDigits = q.replace(/\D/g, "");

      list = list.filter((o) => {
        const orderCode = getOrderCode(o).toLowerCase();
        const rawId = String(o.id || "");
        const name = (o.full_name || "").toLowerCase();
        const email = (o.email || "").toLowerCase();
        const phone = (o.phone || "").replace(/\D/g, "");
        const altPhone = (o.alt_phone || o.alternate_phone || o.altPhone || "").replace(/\D/g, "");
        const rzpPayId = (o.razorpay_payment_id || "").toLowerCase();
        const rzpOrderId = (o.razorpay_order_id || "").toLowerCase();
        const courierTracking = (o.tracking_number || "").toLowerCase();
        const city = (o.city || "").toLowerCase();
        const address = (o.address || "").toLowerCase();

        // Direct string contains
        if (orderCode.includes(q)) return true;
        if (name.includes(q)) return true;
        if (email.includes(q)) return true;
        if (rzpPayId.includes(q)) return true;
        if (rzpOrderId.includes(q)) return true;
        if (courierTracking.includes(q)) return true;
        if (city.includes(q) || address.includes(q)) return true;

        // Numeric digits match for Phone, Alt Phone, and Order ID
        if (qDigits) {
          if (rawId === qDigits || rawId.includes(qDigits)) return true;
          if (phone.includes(qDigits)) return true;
          if (altPhone.includes(qDigits)) return true;
        }

        return false;
      });
    }

    return list;
  }, [dateFilteredOrders, statusFilter, searchQuery]);

  // Reset all active filters
  const resetAllFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setDatePreset("all");
    setStartDate("");
    setEndDate("");
  };

  const isFilterActive =
    searchQuery.trim() !== "" ||
    statusFilter !== "all" ||
    datePreset !== "all" ||
    startDate !== "" ||
    endDate !== "";

  // ============================
  // CONFIRM COD ORDER
  // ============================
  const confirmCODOrder = async (order) => {
    if (
      !window.confirm(
        `Confirm COD Order ${getOrderCode(order)} for ₹${parseFloat(
          order.total_amount
        ).toLocaleString()}?`
      )
    ) {
      return;
    }

    setProcessingPayment(order.id);

    try {
      const res = await fetch(
        `${API_BASE_URL}/orders/confirm-cod/${order.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to confirm COD order");
      }

      alert(data.message || "COD Order confirmed successfully!");
      await fetchOrders();

      if (selectedOrder?.id === order.id) {
        setSelectedOrder((prev) => ({
          ...prev,
          order_status: "confirmed",
        }));
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to confirm COD order");
    } finally {
      setProcessingPayment(null);
    }
  };

  // ============================
  // APPROVE UPI PAYMENT
  // ============================
  const approvePayment = async (order) => {
    if (
      !window.confirm(
        `Approve payment of ₹${parseFloat(
          order.total_amount
        ).toLocaleString()} for Order ${getOrderCode(order)}?`
      )
    ) {
      return;
    }

    setProcessingPayment(order.id);

    try {
      const res = await fetch(
        `${API_BASE_URL}/orders/payment/approve/${order.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            payment_status: "verified",
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to approve payment");
      }

      alert(data.message || "Payment approved successfully");
      await fetchOrders();

      if (selectedOrder?.id === order.id) {
        setSelectedOrder((prev) => ({
          ...prev,
          payment_status: "verified",
          order_status: "confirmed",
        }));
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to approve payment");
    } finally {
      setProcessingPayment(null);
    }
  };

  // ============================
  // REJECT UPI PAYMENT
  // ============================
  const rejectPayment = async (order) => {
    const reason = window.prompt("Enter reason for rejecting this payment:");

    if (reason === null) {
      return;
    }

    if (!reason.trim()) {
      alert("Please enter a rejection reason.");
      return;
    }

    setProcessingPayment(order.id);

    try {
      const res = await fetch(
        `${API_BASE_URL}/orders/payment/reject/${order.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            payment_status: "rejected",
            payment_rejected_reason: reason.trim(),
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to reject payment");
      }

      alert(data.message || "Payment rejected successfully");
      await fetchOrders();

      if (selectedOrder?.id === order.id) {
        setSelectedOrder(null);
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to reject payment");
    } finally {
      setProcessingPayment(null);
    }
  };

  // ============================
  // UPDATE ORDER STATUS (PACKED, SHIPPED, DELIVERED, ETC.)
  // ============================
  const updateOrderStatus = async (orderId, newStatus, courierData = {}) => {
    if (!newStatus) return;

    // If changing to shipped and no tracking info yet, open modal instead
    if (newStatus === "shipped" && !courierData.tracking_number) {
      const ord = orders.find((o) => o.id === orderId) || selectedOrder;
      if (ord) {
        openShippingModal(ord);
        return;
      }
    }

    setUpdatingStatus(orderId);

    try {
      const res = await fetch(`${API_BASE_URL}/orders/status/${orderId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          order_status: newStatus,
          courier_name: courierData.courier_name,
          tracking_number: courierData.tracking_number,
          tracking_url: courierData.tracking_url,
          payment_status: courierData.payment_status,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to update order status");
      }

      alert(data.message || `Order status updated to ${newStatus}`);
      await fetchOrders();

      if (selectedOrder?.id === orderId) {
        setSelectedOrder(data.order || { ...selectedOrder, order_status: newStatus, ...courierData });
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to update order status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const openShippingModal = (order) => {
    setShippingModal({
      open: true,
      order,
      courier_name: order.courier_name || "Delhivery",
      tracking_number: order.tracking_number || "",
      tracking_url: order.tracking_url || "",
    });
  };

  const handleSaveShipping = async () => {
    if (!shippingModal.order) return;

    if (!shippingModal.tracking_number.trim()) {
      alert("Please enter a tracking / AWB number.");
      return;
    }

    await updateOrderStatus(shippingModal.order.id, "shipped", {
      courier_name: shippingModal.courier_name,
      tracking_number: shippingModal.tracking_number.trim(),
      tracking_url: shippingModal.tracking_url.trim(),
    });

    setShippingModal({
      open: false,
      order: null,
      courier_name: "Delhivery",
      tracking_number: "",
      tracking_url: "",
    });
  };

  // ============================
  // DELETE ORDER (WITH HIGHLY CONFIRMING PATTERN ALERT)
  // ============================
  const openDeleteModal = (order) => {
    if (!order) return;
    setDeleteModal({
      open: true,
      order,
      confirmInput: "",
      isDeleting: false,
      errorMessage: "",
    });
  };

  const closeDeleteModal = () => {
    if (deleteModal.isDeleting) return;
    setDeleteModal({
      open: false,
      order: null,
      confirmInput: "",
      isDeleting: false,
      errorMessage: "",
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.order) return;

    const targetOrder = deleteModal.order;
    const targetOrderCode = getOrderCode(targetOrder);
    const inputVal = deleteModal.confirmInput.trim();

    const isMatch =
      inputVal.toUpperCase() === "DELETE" ||
      inputVal.toUpperCase() === targetOrderCode.toUpperCase() ||
      inputVal.toUpperCase() === `DELETE ${targetOrderCode}`.toUpperCase();

    if (!isMatch) {
      setDeleteModal((prev) => ({
        ...prev,
        errorMessage: `Pattern does not match. Please type "${targetOrderCode}" or "DELETE" exactly.`,
      }));
      return;
    }

    setDeleteModal((prev) => ({ ...prev, isDeleting: true, errorMessage: "" }));

    try {
      const res = await fetch(`${API_BASE_URL}/orders/delete/${targetOrder.id}`, {
        method: "DELETE",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || data.error || "Failed to delete order");
      }

      alert(`Order ${targetOrderCode} has been permanently deleted.`);

      // If the currently viewed order was deleted, close the view modal
      if (selectedOrder?.id === targetOrder.id) {
        setSelectedOrder(null);
      }

      // Close delete modal
      setDeleteModal({
        open: false,
        order: null,
        confirmInput: "",
        isDeleting: false,
        errorMessage: "",
      });

      // Refresh orders list and update stats
      await fetchOrders();
    } catch (err) {
      console.error("Delete order error:", err);
      setDeleteModal((prev) => ({
        ...prev,
        isDeleting: false,
        errorMessage: err.message || "Failed to delete order from server.",
      }));
    }
  };

  const currentDeleteOrderCode = deleteModal.order ? getOrderCode(deleteModal.order) : "";
  const isDeletePatternMatched =
    Boolean(deleteModal.confirmInput.trim()) &&
    (deleteModal.confirmInput.trim().toUpperCase() === "DELETE" ||
      deleteModal.confirmInput.trim().toUpperCase() === currentDeleteOrderCode.toUpperCase() ||
      deleteModal.confirmInput.trim().toUpperCase() === `DELETE ${currentDeleteOrderCode}`.toUpperCase());

  // ============================
  // HELPERS & STYLES
  // ============================
  const formatItems = (items) => {
    if (!items || items.length === 0) {
      return "No items";
    }
    return items
      .map((item) => {
        const pid =
          item.product_id ||
          item.productId ||
          (item.id && !String(item.id).startsWith("guest_") ? item.id : "");
        const colour = item.colour || (item.variant && item.variant.colour) || "";
        const pidPrefix = pid ? `[PID #${pid}] ` : "";
        const colourSuffix = colour ? ` (${colour})` : "";
        return `${pidPrefix}${item.name}${colourSuffix} (x${item.quantity})`;
      })
      .join(", ");
  };

  const getPaymentStatusStyle = (status) => {
    switch ((status || "").toLowerCase()) {
      case "paid":
      case "verified":
        return { background: "#d4edda", color: "#155724", border: "1px solid #c3e6cb" };
      case "rejected":
        return { background: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb" };
      default:
        return { background: "#fff3cd", color: "#856404", border: "1px solid #ffeeba" };
    }
  };

  const getOrderStatusStyle = (status) => {
    switch ((status || "").toLowerCase()) {
      case "delivered":
        return { background: "#d1e7dd", color: "#0f5132", border: "1px solid #badbcc" };
      case "shipped":
        return { background: "#fff3cd", color: "#664d03", border: "1px solid #ffecb5" };
      case "packed":
        return { background: "#cff4fc", color: "#055160", border: "1px solid #b6effb" };
      case "confirmed":
        return { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #c8e6c9" };
      case "cancelled":
      case "payment_rejected":
        return { background: "#f8d7da", color: "#842029", border: "1px solid #f5c2c7" };
      default:
        return { background: "#f3e8ff", color: "#6b21a8", border: "1px solid #e9d5ff" };
    }
  };

  const getPaymentScreenshot = (order) => {
    return order.payment_screenshot || order.payment_screenshot_url || null;
  };

  return (
    <div
      className="orders-container"
      style={{
        padding: "24px 30px",
        fontFamily: "'Segoe UI', Roboto, sans-serif",
        background: "#f4f6f8",
        minHeight: "100vh",
      }}
    >
      {/* PAGE HEADER */}
      <div
        className="orders-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "Playfair Display, serif",
              color: "#063b2a",
              margin: "0 0 6px",
              fontSize: 28,
            }}
          >
            Admin Orders & Courier Management
          </h1>
          <p style={{ margin: 0, color: "#666", fontSize: 14 }}>
            Monitor bookings, manage couriers & tracking, and filter customer orders in real-time.
          </p>
        </div>

        <button
          className="orders-refresh-btn"
          onClick={fetchOrders}
          disabled={loading}
          style={{
            background: "#063b2a",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 18px",
            cursor: loading ? "not-allowed" : "pointer",
            fontWeight: "bold",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 8,
            boxShadow: "0 2px 6px rgba(6,59,42,0.2)",
          }}
        >
          <RefreshCw size={14} className={loading ? "spin-icon" : ""} />
          <span>{loading ? "Refreshing..." : "↻ Refresh Orders"}</span>
        </button>
      </div>

      {/* ==============================================================
          ORDER STATS CARDS (DYNAMICALLY COMPUTED FROM DATE FILTER)
      ============================================================== */}
      <div
        className="orders-stats-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 12,
          marginBottom: 22,
        }}
      >
        {/* TOTAL ORDERS */}
        <div
          className="orders-stat-card"
          onClick={() => setStatusFilter("all")}
          style={{
            ...statCardStyle,
            borderColor: statusFilter === "all" ? "#063b2a" : "#e0e0e0",
            backgroundColor: statusFilter === "all" ? "#f0fdf4" : "#ffffff",
          }}
          title="Click to view all orders"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="orders-stat-title" style={statCardTitle}>TOTAL</span>
            <Layers size={16} color="#063b2a" />
          </div>
          <div className="orders-stat-value" style={{ fontSize: 24, fontWeight: "bold", color: "#063b2a", marginTop: 4 }}>
            {stats.total}
          </div>
          <div className="orders-stat-sub" style={{ fontSize: 11, color: "#777", marginTop: 4 }}>
            ₹{stats.totalRevenue.toLocaleString("en-IN", { maximumFractionDigits: 0 })} volume
          </div>
        </div>

        {/* CONFIRMED */}
        <div
          className="orders-stat-card"
          onClick={() => setStatusFilter(statusFilter === "confirmed" ? "all" : "confirmed")}
          style={{
            ...statCardStyle,
            borderColor: statusFilter === "confirmed" ? "#15803d" : "#e0e0e0",
            backgroundColor: statusFilter === "confirmed" ? "#f0fdf4" : "#ffffff",
          }}
          title="Click to filter Confirmed orders"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="orders-stat-title" style={{ ...statCardTitle, color: "#15803d" }}>CONFIRMED</span>
            <CheckCircle2 size={16} color="#15803d" />
          </div>
          <div className="orders-stat-value" style={{ fontSize: 24, fontWeight: "bold", color: "#15803d", marginTop: 4 }}>
            {stats.confirmed}
          </div>
          <div className="orders-stat-sub" style={{ fontSize: 11, color: "#777", marginTop: 4 }}>Ready to pack</div>
        </div>

        {/* PACKED */}
        <div
          className="orders-stat-card"
          onClick={() => setStatusFilter(statusFilter === "packed" ? "all" : "packed")}
          style={{
            ...statCardStyle,
            borderColor: statusFilter === "packed" ? "#0284c7" : "#e0e0e0",
            backgroundColor: statusFilter === "packed" ? "#f0f9ff" : "#ffffff",
          }}
          title="Click to filter Packed orders"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="orders-stat-title" style={{ ...statCardTitle, color: "#0284c7" }}>PACKED</span>
            <Package size={16} color="#0284c7" />
          </div>
          <div className="orders-stat-value" style={{ fontSize: 24, fontWeight: "bold", color: "#0284c7", marginTop: 4 }}>
            {stats.packed}
          </div>
          <div className="orders-stat-sub" style={{ fontSize: 11, color: "#777", marginTop: 4 }}>Awaiting courier</div>
        </div>

        {/* SHIPPED */}
        <div
          className="orders-stat-card"
          onClick={() => setStatusFilter(statusFilter === "shipped" ? "all" : "shipped")}
          style={{
            ...statCardStyle,
            borderColor: statusFilter === "shipped" ? "#b45309" : "#e0e0e0",
            backgroundColor: statusFilter === "shipped" ? "#fffbeb" : "#ffffff",
          }}
          title="Click to filter Shipped orders"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="orders-stat-title" style={{ ...statCardTitle, color: "#b45309" }}>SHIPPED</span>
            <Truck size={16} color="#b45309" />
          </div>
          <div className="orders-stat-value" style={{ fontSize: 24, fontWeight: "bold", color: "#b45309", marginTop: 4 }}>
            {stats.shipped}
          </div>
          <div className="orders-stat-sub" style={{ fontSize: 11, color: "#777", marginTop: 4 }}>In transit</div>
        </div>

        {/* DELIVERED */}
        <div
          className="orders-stat-card"
          onClick={() => setStatusFilter(statusFilter === "delivered" ? "all" : "delivered")}
          style={{
            ...statCardStyle,
            borderColor: statusFilter === "delivered" ? "#047857" : "#e0e0e0",
            backgroundColor: statusFilter === "delivered" ? "#ecfdf5" : "#ffffff",
          }}
          title="Click to filter Delivered orders"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="orders-stat-title" style={{ ...statCardTitle, color: "#047857" }}>DELIVERED</span>
            <CheckCircle2 size={16} color="#047857" />
          </div>
          <div className="orders-stat-value" style={{ fontSize: 24, fontWeight: "bold", color: "#047857", marginTop: 4 }}>
            {stats.delivered}
          </div>
          <div className="orders-stat-sub" style={{ fontSize: 11, color: "#777", marginTop: 4 }}>Completed orders</div>
        </div>

        {/* CANCELLED */}
        <div
          className="orders-stat-card"
          onClick={() => setStatusFilter(statusFilter === "cancelled" ? "all" : "cancelled")}
          style={{
            ...statCardStyle,
            borderColor: statusFilter === "cancelled" ? "#b91c1c" : "#e0e0e0",
            backgroundColor: statusFilter === "cancelled" ? "#fef2f2" : "#ffffff",
          }}
          title="Click to filter Cancelled orders"
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="orders-stat-title" style={{ ...statCardTitle, color: "#b91c1c" }}>CANCELLED</span>
            <XCircle size={16} color="#b91c1c" />
          </div>
          <div className="orders-stat-value" style={{ fontSize: 24, fontWeight: "bold", color: "#b91c1c", marginTop: 4 }}>
            {stats.cancelled}
          </div>
          <div className="orders-stat-sub" style={{ fontSize: 11, color: "#777", marginTop: 4 }}>Void / rejected</div>
        </div>
      </div>

      {/* ==============================================================
          FILTER TOOLBAR (SEARCH + STATUS FILTER + DATE RANGE PRESETS)
      ============================================================== */}
      <div
        className="orders-filter-box"
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 16,
          marginBottom: 20,
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <div
          className="orders-filter-top"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          {/* SEARCH INPUT */}
          <div
            className="orders-search-box"
            style={{
              flex: "2 1 320px",
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Search
              size={18}
              color="#888"
              style={{ position: "absolute", left: 12, pointerEvents: "none" }}
            />
            <input
              type="text"
              placeholder="Search by Phone, Alt Phone, Email, Order ID, Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 36px 10px 38px",
                borderRadius: 8,
                border: "1.5px solid #d1d5db",
                fontSize: 13,
                outline: "none",
                background: "#fafafa",
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{
                  position: "absolute",
                  right: 10,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#888",
                  padding: 2,
                }}
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* STATUS FILTER DROPDOWN */}
          <div className="orders-status-box" style={{ flex: "1 1 180px" }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1.5px solid #d1d5db",
                fontSize: 13,
                outline: "none",
                background: "#fafafa",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              <option value="all">All Order Statuses</option>
              <option value="confirmed">● Confirmed</option>
              <option value="packed">● Packed</option>
              <option value="shipped">● Shipped 🚚</option>
              <option value="delivered">● Delivered ✓</option>
              <option value="pending">● Pending / Payment Pending</option>
              <option value="cancelled">● Cancelled / Rejected</option>
            </select>
          </div>

          {/* RESET ALL BUTTON */}
          {isFilterActive && (
            <button
              className="orders-reset-btn"
              onClick={resetAllFilters}
              style={{
                padding: "10px 14px",
                background: "#fee2e2",
                color: "#991b1b",
                border: "1px solid #fecaca",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <X size={14} /> Clear All Filters
            </button>
          )}
        </div>

        {/* DATE RANGE FILTER ROW */}
        <div
          className="orders-date-row"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
            paddingTop: 10,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          {/* Quick Date Presets */}
          <div className="orders-date-presets" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: "#666", fontWeight: 600, marginRight: 4, flexShrink: 0 }}>
              <Calendar size={13} style={{ verticalAlign: "middle", marginRight: 4 }} />
              Date Filter:
            </span>

            {[
              { id: "all", label: "All Time" },
              { id: "today", label: "Today" },
              { id: "yesterday", label: "Yesterday" },
              { id: "week", label: "Last 7 Days" },
              { id: "month", label: "This Month" },
              { id: "custom", label: "Custom Range" },
            ].map((p) => {
              const isActive = datePreset === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => handleDatePreset(p.id)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 20,
                    border: isActive ? "1px solid #063b2a" : "1px solid #d1d5db",
                    background: isActive ? "#063b2a" : "#fff",
                    color: isActive ? "#fff" : "#4b5563",
                    fontSize: 12,
                    cursor: "pointer",
                    fontWeight: isActive ? 600 : 500,
                    transition: "all 0.15s ease",
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Custom Date Inputs */}
          <div className="orders-custom-dates" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 11, color: "#777" }}>From:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setDatePreset("custom");
                }}
                style={{
                  padding: "5px 8px",
                  fontSize: 12,
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  background: "#fafafa",
                }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 11, color: "#777" }}>To:</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setDatePreset("custom");
                }}
                style={{
                  padding: "5px 8px",
                  fontSize: 12,
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  background: "#fafafa",
                }}
              />
            </div>

            {(startDate || endDate) && (
              <button
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  setDatePreset("all");
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#dc2626",
                  fontSize: 11,
                  cursor: "pointer",
                  fontWeight: 600,
                  padding: "4px 8px",
                }}
              >
                Clear Date
              </button>
            )}
          </div>
        </div>

        {/* ACTIVE FILTER SUMMARY BANNER */}
        <div
          className="orders-filter-summary"
          style={{
            marginTop: 10,
            fontSize: 12,
            color: "#6b7280",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            Showing <strong>{displayedOrders.length}</strong> of <strong>{orders.length}</strong> orders
            {datePreset !== "all" && (
              <span style={{ color: "#063b2a", fontWeight: 600 }}>
                {" "}• Filtered by Date ({startDate || "Start"} to {endDate || "End"})
              </span>
            )}
            {statusFilter !== "all" && (
              <span style={{ color: "#063b2a", fontWeight: 600 }}>
                {" "}• Status: {statusFilter.toUpperCase()}
              </span>
            )}
            {searchQuery && (
              <span style={{ color: "#063b2a", fontWeight: 600 }}>
                {" "}• Search query: "{searchQuery}"
              </span>
            )}
          </span>
        </div>
      </div>

      {/* ==============================================================
          ORDERS TABLE
      ============================================================== */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#063b2a" }}>
          <RefreshCw size={28} className="spin-icon" style={{ marginBottom: 12 }} />
          <h3>Loading orders...</h3>
        </div>
      ) : displayedOrders.length === 0 ? (
        <div style={{ background: "#fff", padding: 50, borderRadius: 12, textAlign: "center" }}>
          <AlertCircle size={36} color="#d97706" style={{ marginBottom: 10 }} />
          <h3 style={{ margin: "0 0 8px", color: "#374151" }}>No orders found</h3>
          <p style={{ color: "#6b7280", margin: "0 0 16px", fontSize: 14 }}>
            {isFilterActive
              ? "No orders match the current search or date range filters."
              : "No orders have been recorded in the database yet."}
          </p>
          {isFilterActive && (
            <button
              onClick={resetAllFilters}
              style={{
                padding: "8px 18px",
                background: "#063b2a",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                fontWeight: 600,
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Reset Filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div
            className="table-scroll-wrapper orders-desktop-table-wrap"
          style={{
            maxHeight: "75vh",
            overflowY: "auto",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            background: "#fff",
            borderRadius: 12,
            boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
            border: "1px solid #e0e0e0",
            width: "100%",
          }}
        >
          <table
            style={{
              width: "100%",
              minWidth: 1450,
              borderCollapse: "collapse",
              fontSize: 13,
            }}
          >
            <thead
              style={{
                background: "#063b2a",
                color: "#fff",
                position: "sticky",
                top: 0,
                zIndex: 2,
              }}
            >
              <tr>
                <th style={thStyle}>Order ID</th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Address</th>
                <th style={thStyle}>Items</th>
                <th style={thStyle}>Total</th>
                <th style={thStyle}>Payment Method</th>
                <th style={thStyle}>Payment Status</th>
                <th style={thStyle}>Order Status</th>
                <th style={thStyle}>Courier Tracking</th>
                <th style={thStyle}>Actions</th>
                <th style={thStyle}>Details</th>
              </tr>
            </thead>

            <tbody>
              {displayedOrders.map((order) => {
                const screenshot = getPaymentScreenshot(order);
                const isCod = order.payment_method === "cod";
                const isOrderPending =
                  !order.order_status ||
                  order.order_status === "pending" ||
                  order.order_status === "payment_pending";

                const isProcessing = processingPayment === order.id || updatingStatus === order.id;

                return (
                  <tr
                    key={order.id}
                    style={{
                      background: "#fff",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    {/* ORDER ID & DATE (DD-MM-YYYY) */}
                    <td style={tdStyle}>
                      <strong style={{ color: "#063b2a", fontSize: 14 }}>{getOrderCode(order)}</strong>
                      <div
                        style={{
                          color: "#555",
                          fontSize: 12,
                          marginTop: 4,
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                        title={formatDateTimeDDMMYYYY(order.created_at)}
                      >
                        <Clock size={11} color="#888" />
                        {formatDateDDMMYYYY(order.created_at)}
                      </div>
                    </td>

                    {/* CUSTOMER */}
                    <td style={tdStyle}>
                      <div>
                        <strong>{order.full_name}</strong>
                      </div>
                      <div style={{ color: "#444", fontSize: 12, marginTop: 2 }}>
                        📞 {order.phone}
                      </div>
                      {(order.alt_phone || order.alternate_phone || order.altPhone) && (
                        <div
                          style={{
                            color: "#0284c7",
                            fontSize: 11,
                            marginTop: 2,
                            fontWeight: 500,
                          }}
                          title="Alternate Phone"
                        >
                          Alt: {order.alt_phone || order.alternate_phone || order.altPhone}
                        </div>
                      )}
                      {order.email && (
                        <div style={{ color: "#888", fontSize: 11, marginTop: 2 }}>{order.email}</div>
                      )}
                    </td>

                    {/* ADDRESS */}
                    <td style={{ ...tdStyle, maxWidth: 200, lineHeight: 1.4 }}>
                      {order.address}, {order.city}, {order.state} - {order.pin_code}
                    </td>

                    {/* ITEMS */}
                    <td style={{ ...tdStyle, maxWidth: 220 }} title={formatItems(order.items)}>
                      {order.items && order.items.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {order.items.map((it, idx) => {
                            const pid =
                              it.product_id ||
                              it.productId ||
                              (it.id && !String(it.id).startsWith("guest_") ? it.id : "");
                            const colour = it.colour || (it.variant && it.variant.colour) || "";
                            return (
                              <div key={idx} style={{ fontSize: 12, lineHeight: 1.3 }}>
                                {pid && (
                                  <span
                                    style={{
                                      background: "#063b2a",
                                      color: "#fff",
                                      padding: "1px 5px",
                                      borderRadius: 3,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      marginRight: 4,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    #{pid}
                                  </span>
                                )}
                                <span style={{ fontWeight: 600, color: "#111" }}>{it.name}</span>
                                {colour && (
                                  <span style={{ color: "#0284c7", fontSize: 11, fontWeight: 600 }}>
                                    {" "}• {colour}
                                  </span>
                                )}
                                <span style={{ color: "#555", fontWeight: 700 }}> (x{it.quantity})</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span style={{ color: "#777" }}>No items</span>
                      )}
                    </td>

                    {/* TOTAL */}
                    <td style={tdStyle}>
                      <strong style={{ color: "#063b2a" }}>
                        ₹{parseFloat(order.total_amount || 0).toLocaleString("en-IN")}
                      </strong>
                    </td>

                    {/* PAYMENT METHOD */}
                    <td style={tdStyle}>
                      {order.payment_method === "razorpay" ? (
                        <div>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              borderRadius: 14,
                              fontWeight: "bold",
                              fontSize: 11,
                              background: "#ecfdf5",
                              color: "#047857",
                              border: "1px solid #a7f3d0",
                            }}
                          >
                            ⚡ Razorpay Online
                          </span>
                          {order.razorpay_payment_id && (
                            <div
                              style={{
                                fontSize: 10,
                                color: "#555",
                                marginTop: 4,
                                fontFamily: "monospace",
                              }}
                              title={order.razorpay_payment_id}
                            >
                              {order.razorpay_payment_id}
                            </div>
                          )}
                        </div>
                      ) : isCod ? (
                        <span
                          style={{
                            display: "inline-block",
                            padding: "4px 10px",
                            borderRadius: 14,
                            fontWeight: "bold",
                            fontSize: 11,
                            background: "#e0f2fe",
                            color: "#0369a1",
                            border: "1px solid #bae6fd",
                          }}
                        >
                          💵 Cash On Delivery
                        </span>
                      ) : (
                        <div>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 10px",
                              borderRadius: 14,
                              fontWeight: "bold",
                              fontSize: 11,
                              background: "#f3e8ff",
                              color: "#6b21a8",
                              border: "1px solid #e9d5ff",
                            }}
                          >
                            📱 UPI Online
                          </span>

                          {screenshot && (
                            <div style={{ marginTop: 6 }}>
                              <img
                                src={screenshot}
                                alt="Screenshot"
                                onClick={() => setPreviewImage(screenshot)}
                                style={{
                                  width: 50,
                                  height: 60,
                                  objectFit: "cover",
                                  borderRadius: 4,
                                  border: "1px solid #063b2a",
                                  cursor: "pointer",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </td>

                    {/* PAYMENT STATUS */}
                    <td style={tdStyle}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "5px 10px",
                          borderRadius: 20,
                          fontWeight: "bold",
                          fontSize: 11,
                          textTransform: "capitalize",
                          ...getPaymentStatusStyle(order.payment_status),
                        }}
                      >
                        {order.payment_status || "pending"}
                      </span>

                      {order.payment_rejected_reason && (
                        <div style={{ marginTop: 4, fontSize: 11, color: "#721c24", maxWidth: 130 }}>
                          Reason: {order.payment_rejected_reason}
                        </div>
                      )}
                    </td>

                    {/* ORDER STATUS */}
                    <td style={tdStyle}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "5px 12px",
                          borderRadius: 20,
                          fontWeight: "bold",
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          ...getOrderStatusStyle(order.order_status),
                        }}
                      >
                        ● {order.order_status || "Pending"}
                      </span>
                    </td>

                    {/* COURIER TRACKING */}
                    <td style={{ ...tdStyle, minWidth: 150 }}>
                      {order.courier_name || order.tracking_number ? (
                        <div>
                          <div style={{ fontWeight: 600, color: "#333" }}>{order.courier_name}</div>
                          {order.tracking_number && (
                            <div style={{ fontSize: 11, color: "#555", fontFamily: "monospace" }}>
                              AWB: {order.tracking_number}
                            </div>
                          )}
                          <div style={{ marginTop: 4, display: "flex", gap: 6 }}>
                            {order.tracking_url && (
                              <a
                                href={order.tracking_url}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  fontSize: 11,
                                  color: "#0369a1",
                                  textDecoration: "underline",
                                }}
                              >
                                Track ↗
                              </a>
                            )}
                            <button
                              onClick={() => openShippingModal(order)}
                              style={{
                                border: "none",
                                background: "none",
                                color: "#063b2a",
                                fontSize: 11,
                                cursor: "pointer",
                                textDecoration: "underline",
                                padding: 0,
                              }}
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => openShippingModal(order)}
                          style={{
                            padding: "4px 8px",
                            background: "#e8f0fe",
                            color: "#1a73e8",
                            border: "1px solid #c2dbfe",
                            borderRadius: 5,
                            cursor: "pointer",
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          + Add Courier
                        </button>
                      )}
                    </td>

                    {/* ACTIONS */}
                    <td style={{ ...tdStyle, minWidth: 170 }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {/* 1. APPROVAL ACTION (IF PENDING) */}
                        {isOrderPending && (
                          <>
                            {order.payment_method === "razorpay" ? (
                              <div
                                style={{
                                  padding: "5px 10px",
                                  background: "#ecfdf5",
                                  color: "#047857",
                                  border: "1px solid #a7f3d0",
                                  borderRadius: 5,
                                  fontWeight: "bold",
                                  fontSize: 11,
                                  textAlign: "center",
                                }}
                              >
                                ✓ Instant Paid Online
                              </div>
                            ) : isCod ? (
                              <button
                                disabled={isProcessing}
                                onClick={() => confirmCODOrder(order)}
                                style={{
                                  padding: "6px 12px",
                                  background: isProcessing ? "#aaa" : "#0f5132",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 5,
                                  cursor: isProcessing ? "not-allowed" : "pointer",
                                  fontWeight: "bold",
                                  fontSize: 12,
                                }}
                              >
                                {isProcessing ? "Processing..." : "✓ Confirm COD"}
                              </button>
                            ) : (
                              <div style={{ display: "flex", gap: 4 }}>
                                <button
                                  disabled={isProcessing || !screenshot}
                                  onClick={() => approvePayment(order)}
                                  title={!screenshot ? "Screenshot not uploaded yet" : "Approve Payment"}
                                  style={{
                                    flex: 1,
                                    padding: "6px 8px",
                                    background:
                                      isProcessing || !screenshot ? "#aaa" : "#198754",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 5,
                                    cursor:
                                      isProcessing || !screenshot ? "not-allowed" : "pointer",
                                    fontWeight: "bold",
                                    fontSize: 11,
                                  }}
                                >
                                  Approve
                                </button>
                                <button
                                  disabled={isProcessing}
                                  onClick={() => rejectPayment(order)}
                                  style={{
                                    flex: 1,
                                    padding: "6px 8px",
                                    background: isProcessing ? "#aaa" : "#dc3545",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 5,
                                    cursor: isProcessing ? "not-allowed" : "pointer",
                                    fontWeight: "bold",
                                    fontSize: 11,
                                  }}
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                          </>
                        )}

                        {/* 2. LIVE STATUS DROPDOWN */}
                        <select
                          disabled={isProcessing}
                          value={order.order_status || "pending"}
                          onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                          style={{
                            padding: "5px 8px",
                            borderRadius: 5,
                            border: "1px solid #ccc",
                            fontSize: 12,
                            background: "#fafafa",
                            cursor: "pointer",
                          }}
                        >
                          <option value="pending">Status: Pending</option>
                          <option value="confirmed">Status: Confirmed</option>
                          <option value="packed">Status: Packed</option>
                          <option value="shipped">Status: Shipped 🚚</option>
                          <option value="delivered">Status: Delivered ✓</option>
                          <option value="cancelled">Status: Cancelled</option>
                        </select>
                      </div>
                    </td>

                    {/* DETAILS & ACTIONS */}
                    <td style={tdStyle}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <button
                          onClick={() => setSelectedOrder(order)}
                          style={{
                            padding: "6px 14px",
                            background: "#063b2a",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            fontWeight: "bold",
                            fontSize: 12,
                            transition: "background 0.2s",
                          }}
                        >
                          View
                        </button>
                        <button
                          onClick={() => openDeleteModal(order)}
                          title={`Delete order ${getOrderCode(order)}`}
                          style={{
                            padding: "6px 8px",
                            background: "#fff",
                            color: "#dc2626",
                            border: "1px solid #fecaca",
                            borderRadius: 6,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.15s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#fee2e2";
                            e.currentTarget.style.borderColor = "#dc2626";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#fff";
                            e.currentTarget.style.borderColor = "#fecaca";
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ==============================================================
            MOBILE ORDER CARDS (RENDERED ON MOBILE INSTEAD OF 1450px TABLE)
        ============================================================== */}
        <div className="orders-mobile-cards-list">
          {displayedOrders.map((order) => {
            const screenshot = getPaymentScreenshot(order);
            const isCod = order.payment_method === "cod";
            const isOrderPending =
              !order.order_status ||
              order.order_status === "pending" ||
              order.order_status === "payment_pending";
            const isProcessing = processingPayment === order.id || updatingStatus === order.id;

            return (
              <div
                key={order.id}
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  padding: "14px 14px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {/* TOP ROW: Order Code, Status Badge, Date & Total */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16, fontWeight: "bold", color: "#063b2a", letterSpacing: 0.5 }}>
                        {getOrderCode(order)}
                      </span>
                      <span
                        style={{
                          padding: "3px 8px",
                          borderRadius: 14,
                          fontWeight: "bold",
                          fontSize: 10,
                          textTransform: "uppercase",
                          ...getOrderStatusStyle(order.order_status),
                        }}
                      >
                        ● {order.order_status || "Pending"}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        color: "#64748b",
                        fontSize: 11,
                        marginTop: 4,
                        fontWeight: 500,
                      }}
                    >
                      <Clock size={11} color="#64748b" />
                      <span>{formatDateTimeDDMMYYYY(order.created_at)}</span>
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 17, fontWeight: "bold", color: "#063b2a" }}>
                      ₹{parseFloat(order.total_amount || 0).toLocaleString("en-IN")}
                    </div>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 7px",
                        borderRadius: 10,
                        fontWeight: 700,
                        fontSize: 10,
                        marginTop: 2,
                        textTransform: "capitalize",
                        ...getPaymentStatusStyle(order.payment_status),
                      }}
                    >
                      {order.payment_status || "pending"}
                    </span>
                  </div>
                </div>

                {/* CUSTOMER INFORMATION */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #eef2f6",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 6,
                    }}
                  >
                    <strong style={{ fontSize: 13, color: "#1e293b" }}>{order.full_name}</strong>
                    {order.city && (
                      <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                        📍 {order.city}
                      </span>
                    )}
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12 }}>
                    {order.phone && (
                      <a
                        href={`tel:${order.phone}`}
                        style={{
                          color: "#063b2a",
                          textDecoration: "none",
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          background: "#e8f0fe",
                          padding: "3px 8px",
                          borderRadius: 6,
                        }}
                      >
                        📞 {order.phone}
                      </a>
                    )}

                    {(order.alt_phone || order.alternate_phone || order.altPhone) && (
                      <a
                        href={`tel:${order.alt_phone || order.alternate_phone || order.altPhone}`}
                        style={{
                          color: "#0284c7",
                          textDecoration: "none",
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          background: "#e0f2fe",
                          padding: "3px 8px",
                          borderRadius: 6,
                        }}
                        title="Alternate Phone"
                      >
                        📱 Alt: {order.alt_phone || order.alternate_phone || order.altPhone}
                      </a>
                    )}
                  </div>

                  {order.address && (
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 6, lineHeight: 1.3 }}>
                      {order.address}, {order.state} - {order.pin_code}
                    </div>
                  )}
                </div>

                {/* ORDER ITEMS */}
                <div style={{ fontSize: 12 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#475569",
                      marginBottom: 4,
                      textTransform: "uppercase",
                    }}
                  >
                    Ordered Items ({order.items ? order.items.length : 0})
                  </div>
                  {order.items && order.items.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {order.items.map((it, idx) => {
                        const pid =
                          it.product_id ||
                          it.productId ||
                          (it.id && !String(it.id).startsWith("guest_") ? it.id : "");
                        const colour = it.colour || (it.variant && it.variant.colour) || "";
                        return (
                          <div
                            key={idx}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              background: "#fafafa",
                              padding: "6px 8px",
                              borderRadius: 6,
                              fontSize: 12,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
                              {pid && (
                                <span
                                  style={{
                                    background: "#063b2a",
                                    color: "#fff",
                                    padding: "1px 5px",
                                    borderRadius: 3,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    fontFamily: "monospace",
                                    flexShrink: 0,
                                  }}
                                >
                                  #{pid}
                                </span>
                              )}
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: "#1e293b",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 160,
                                }}
                              >
                                {it.name}
                              </span>
                              {colour && (
                                <span style={{ color: "#0284c7", fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                                  • {colour}
                                </span>
                              )}
                            </div>
                            <span style={{ fontWeight: 700, color: "#475569", flexShrink: 0 }}>
                              x{it.quantity}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ color: "#94a3b8", fontSize: 11 }}>No items detailed</div>
                  )}
                </div>

                {/* PAYMENT & COURIER ROW */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 8,
                    paddingTop: 6,
                    borderTop: "1px solid #f1f5f9",
                  }}
                >
                  {/* Payment badge */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {order.payment_method === "razorpay" ? (
                      <span
                        style={{
                          display: "inline-block",
                          padding: "3px 8px",
                          borderRadius: 12,
                          fontWeight: "bold",
                          fontSize: 11,
                          background: "#ecfdf5",
                          color: "#047857",
                          border: "1px solid #a7f3d0",
                        }}
                      >
                        ⚡ Razorpay
                      </span>
                    ) : isCod ? (
                      <span
                        style={{
                          display: "inline-block",
                          padding: "3px 8px",
                          borderRadius: 12,
                          fontWeight: "bold",
                          fontSize: 11,
                          background: "#e0f2fe",
                          color: "#0369a1",
                          border: "1px solid #bae6fd",
                        }}
                      >
                        💵 COD
                      </span>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "3px 8px",
                            borderRadius: 12,
                            fontWeight: "bold",
                            fontSize: 11,
                            background: "#f3e8ff",
                            color: "#6b21a8",
                            border: "1px solid #e9d5ff",
                          }}
                        >
                          📱 UPI
                        </span>
                        {screenshot && (
                          <img
                            src={screenshot}
                            alt="Screenshot"
                            onClick={() => setPreviewImage(screenshot)}
                            style={{
                              width: 28,
                              height: 34,
                              objectFit: "cover",
                              borderRadius: 4,
                              border: "1px solid #063b2a",
                              cursor: "pointer",
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Courier info or Add Courier */}
                  <div>
                    {order.courier_name || order.tracking_number ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                        <span style={{ fontWeight: 600, color: "#334155" }}>
                          🚚 {order.courier_name || "Courier"}
                        </span>
                        {order.tracking_url ? (
                          <a
                            href={order.tracking_url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: "#0284c7", fontWeight: 700, textDecoration: "none" }}
                          >
                            Track ↗
                          </a>
                        ) : null}
                        <button
                          onClick={() => openShippingModal(order)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#063b2a",
                            fontWeight: 600,
                            cursor: "pointer",
                            padding: 0,
                            textDecoration: "underline",
                            fontSize: 11,
                          }}
                        >
                          Edit
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => openShippingModal(order)}
                        style={{
                          padding: "3px 8px",
                          background: "#e8f0fe",
                          color: "#1a73e8",
                          border: "1px solid #c2dbfe",
                          borderRadius: 5,
                          cursor: "pointer",
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        + Add Courier
                      </button>
                    )}
                  </div>
                </div>

                {/* ACTIONS ROW */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    paddingTop: 6,
                    borderTop: "1px solid #f1f5f9",
                  }}
                >
                  {/* PENDING ACTIONS (CONFIRM COD OR APPROVE UPI) */}
                  {isOrderPending && (
                    <>
                      {order.payment_method === "razorpay" ? (
                        <div
                          style={{
                            padding: "5px 8px",
                            background: "#ecfdf5",
                            color: "#047857",
                            border: "1px solid #a7f3d0",
                            borderRadius: 6,
                            fontWeight: "bold",
                            fontSize: 11,
                            textAlign: "center",
                          }}
                        >
                          ✓ Instant Online Payment Captured
                        </div>
                      ) : isCod ? (
                        <button
                          disabled={isProcessing}
                          onClick={() => confirmCODOrder(order)}
                          style={{
                            padding: "8px 12px",
                            background: isProcessing ? "#aaa" : "#0f5132",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            cursor: isProcessing ? "not-allowed" : "pointer",
                            fontWeight: "bold",
                            fontSize: 12,
                            width: "100%",
                          }}
                        >
                          {isProcessing ? "Processing..." : "✓ Confirm COD Order"}
                        </button>
                      ) : (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            disabled={isProcessing || !screenshot}
                            onClick={() => approvePayment(order)}
                            style={{
                              flex: 1,
                              padding: "7px 10px",
                              background: isProcessing || !screenshot ? "#aaa" : "#198754",
                              color: "#fff",
                              border: "none",
                              borderRadius: 6,
                              fontWeight: "bold",
                              fontSize: 11,
                            }}
                          >
                            Approve Payment
                          </button>
                          <button
                            disabled={isProcessing}
                            onClick={() => rejectPayment(order)}
                            style={{
                              flex: 1,
                              padding: "7px 10px",
                              background: isProcessing ? "#aaa" : "#dc3545",
                              color: "#fff",
                              border: "none",
                              borderRadius: 6,
                              fontWeight: "bold",
                              fontSize: 11,
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {/* STATUS SELECT & VIEW / DELETE BUTTONS */}
                  <div className="orders-card-actions-row" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <select
                      disabled={isProcessing}
                      value={order.order_status || "pending"}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                      style={{
                        flex: 1,
                        padding: "7px 8px",
                        borderRadius: 6,
                        border: "1px solid #cbd5e1",
                        fontSize: 12,
                        background: "#fafafa",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      <option value="pending">Status: Pending</option>
                      <option value="confirmed">Status: Confirmed</option>
                      <option value="packed">Status: Packed</option>
                      <option value="shipped">Status: Shipped 🚚</option>
                      <option value="delivered">Status: Delivered ✓</option>
                      <option value="cancelled">Status: Cancelled</option>
                    </select>

                    <button
                      onClick={() => setSelectedOrder(order)}
                      style={{
                        padding: "7px 14px",
                        background: "#063b2a",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontWeight: "bold",
                        fontSize: 12,
                      }}
                    >
                      View
                    </button>

                    <button
                      onClick={() => openDeleteModal(order)}
                      title={`Delete order ${getOrderCode(order)}`}
                      style={{
                        padding: "7px 10px",
                        background: "#fff",
                        color: "#dc2626",
                        border: "1px solid #fecaca",
                        borderRadius: 6,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </>
    )}

      {/* =====================================
          SHIPPING & COURIER MODAL
      ===================================== */}
      {shippingModal.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
            padding: 20,
          }}
          onClick={() =>
            setShippingModal({
              open: false,
              order: null,
              courier_name: "Delhivery",
              tracking_number: "",
              tracking_url: "",
            })
          }
        >
          <div
            className="modal-responsive-card"
            style={{
              background: "#fff",
              borderRadius: 14,
              width: "100%",
              maxWidth: 480,
              padding: 26,
              boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 6px", color: "#063b2a" }}>
              🚚 Dispatch / Courier Details
            </h3>
            <p style={{ margin: "0 0 18px", color: "#666", fontSize: 13 }}>
              Order {getOrderCode(shippingModal.order)} • Update courier and live tracking for the customer
            </p>

            <div style={{ marginBottom: 14 }}>
              <label style={modalInputLabel}>Courier Partner</label>
              <select
                value={shippingModal.courier_name}
                onChange={(e) =>
                  setShippingModal({ ...shippingModal, courier_name: e.target.value })
                }
                style={modalInputStyle}
              >
                <option value="Delhivery">Delhivery</option>
                <option value="Blue Dart">Blue Dart</option>
                <option value="DTDC">DTDC</option>
                <option value="India Post">India Post Speed Post</option>
                <option value="Ekart Logistics">Ekart Logistics</option>
                <option value="Shadowfax">Shadowfax</option>
                <option value="The Professional Couriers">The Professional Couriers</option>
                <option value="Other Courier">Other Courier</option>
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={modalInputLabel}>Tracking / AWB Number *</label>
              <input
                type="text"
                placeholder="e.g. 123456789012"
                value={shippingModal.tracking_number}
                onChange={(e) =>
                  setShippingModal({ ...shippingModal, tracking_number: e.target.value })
                }
                style={modalInputStyle}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={modalInputLabel}>Tracking URL (Optional)</label>
              <input
                type="text"
                placeholder="https://www.delhivery.com/track/package/..."
                value={shippingModal.tracking_url}
                onChange={(e) =>
                  setShippingModal({ ...shippingModal, tracking_url: e.target.value })
                }
                style={modalInputStyle}
              />
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() =>
                  setShippingModal({
                    open: false,
                    order: null,
                    courier_name: "Delhivery",
                    tracking_number: "",
                    tracking_url: "",
                  })
                }
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "#eee",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleSaveShipping}
                style={{
                  flex: 2,
                  padding: "10px",
                  background: "#063b2a",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                ✓ Save & Mark as Shipped
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =====================================
          SCREENSHOT PREVIEW MODAL
      ===================================== */}
      {previewImage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 11000,
            padding: 30,
          }}
          onClick={() => setPreviewImage(null)}
        >
          <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreviewImage(null)}
              style={{
                position: "absolute",
                right: -15,
                top: -15,
                width: 35,
                height: 35,
                borderRadius: "50%",
                border: "none",
                background: "#ff5252",
                color: "#fff",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
            <img
              src={previewImage}
              alt="Screenshot Preview"
              style={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                objectFit: "contain",
                borderRadius: 10,
                background: "#fff",
              }}
            />
          </div>
        </div>
      )}

      {/* ==============================================================
          ORDER DETAILS MODAL (WITH BOOKING DATE & TIME + ALTERNATE PHONE)
      ============================================================== */}
      {selectedOrder && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.65)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: 15,
          }}
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="order-details-modal-content"
            style={{
              background: "#fff",
              borderRadius: 15,
              width: "100%",
              maxWidth: 920,
              maxHeight: "92vh",
              overflowY: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* MODAL HEADER */}
            <div
              className="order-details-modal-header"
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                background: "#fff",
                padding: "18px 24px",
                borderBottom: "1px solid #eee",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: "#777", letterSpacing: 0.5 }}>
                  ORDER DETAILS & BOOKING INFORMATION
                </div>
                <h2 style={{ margin: 0, color: "#063b2a", fontSize: 22 }}>
                  Order {getOrderCode(selectedOrder)}
                </h2>
              </div>

              <button
                onClick={() => setSelectedOrder(null)}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  border: "none",
                  background: "#f1f1f1",
                  color: "#333",
                  fontSize: 22,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            {/* MODAL BODY */}
            <div className="order-details-modal-body" style={{ padding: 24 }}>
              {/* BOOKING DATE & TIME BANNER */}
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 10,
                  padding: "14px 18px",
                  marginBottom: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      background: "#063b2a",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Clock size={18} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: "#166534", fontWeight: 700, textTransform: "uppercase" }}>
                      Date & Time of Booking
                    </div>
                    <div style={{ fontSize: 15, fontWeight: "bold", color: "#063b2a" }}>
                      {formatDateTimeDDMMYYYY(selectedOrder.created_at)}
                    </div>
                  </div>
                </div>

                <div style={{ fontSize: 12, color: "#166534", background: "#dcfce7", padding: "4px 10px", borderRadius: 14 }}>
                  Order DB ID: #{selectedOrder.id}
                </div>
              </div>

              {/* STAGE & STATUS CONTROLS */}
              <div
                style={{
                  background: "#f9fcfb",
                  border: "1px solid #cce5dd",
                  borderRadius: 10,
                  padding: 16,
                  marginBottom: 24,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 11, color: "#555", fontWeight: 600 }}>CURRENT STAGE</div>
                  <div style={{ marginTop: 4 }}>
                    <span
                      style={{
                        padding: "6px 14px",
                        borderRadius: 20,
                        fontWeight: "bold",
                        fontSize: 13,
                        textTransform: "uppercase",
                        ...getOrderStatusStyle(selectedOrder.order_status),
                      }}
                    >
                      ● {selectedOrder.order_status || "Pending"}
                    </span>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    onClick={() => updateOrderStatus(selectedOrder.id, "packed")}
                    style={{
                      padding: "8px 14px",
                      background: "#e8f0fe",
                      color: "#1a73e8",
                      border: "1px solid #c2dbfe",
                      borderRadius: 6,
                      fontWeight: "bold",
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    Pack Order
                  </button>

                  <button
                    onClick={() => openShippingModal(selectedOrder)}
                    style={{
                      padding: "8px 14px",
                      background: "#fef7e0",
                      color: "#b06000",
                      border: "1px solid #fde293",
                      borderRadius: 6,
                      fontWeight: "bold",
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    🚚 Dispatch / Ship (Courier)
                  </button>

                  <button
                    onClick={() => updateOrderStatus(selectedOrder.id, "delivered")}
                    style={{
                      padding: "8px 14px",
                      background: "#e6f4ea",
                      color: "#137333",
                      border: "1px solid #ceead6",
                      borderRadius: 6,
                      fontWeight: "bold",
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    ✓ Mark Delivered
                  </button>
                </div>
              </div>

              {/* COURIER INFO IF SHIPPED */}
              {(selectedOrder.courier_name || selectedOrder.tracking_number) && (
                <div
                  style={{
                    background: "#fdf8ee",
                    border: "1px solid #fae1af",
                    borderRadius: 10,
                    padding: 16,
                    marginBottom: 24,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 12,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 11, color: "#8c5b1b", fontWeight: 700 }}>
                      COURIER SHIPMENT DETAILS
                    </div>
                    <div style={{ marginTop: 4, fontSize: 14, color: "#333" }}>
                      Partner: <strong>{selectedOrder.courier_name}</strong>
                      {selectedOrder.tracking_number && (
                        <span> • AWB: <strong>{selectedOrder.tracking_number}</strong></span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 10 }}>
                    {selectedOrder.tracking_url && (
                      <a
                        href={selectedOrder.tracking_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          padding: "6px 12px",
                          background: "#063b2a",
                          color: "#fff",
                          borderRadius: 6,
                          fontSize: 12,
                          textDecoration: "none",
                          fontWeight: 600,
                        }}
                      >
                        Track Shipment ↗
                      </a>
                    )}

                    <button
                      onClick={() => openShippingModal(selectedOrder)}
                      style={{
                        padding: "6px 12px",
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: 6,
                        fontSize: 12,
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                    >
                      Edit Courier
                    </button>
                  </div>
                </div>
              )}

              {/* CUSTOMER INFORMATION (WITH PRIMARY & ALTERNATE PHONE) */}
              <h3 style={{ color: "#063b2a", margin: "0 0 12px", fontSize: 16 }}>
                Customer Information
              </h3>
              <div
                className="modal-customer-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 12,
                  marginBottom: 24,
                }}
              >
                <div style={infoCardStyle}>
                  <div style={infoLabelStyle}>Full Name</div>
                  <div style={infoValueStyle}>{selectedOrder.full_name || "-"}</div>
                </div>

                <div style={infoCardStyle}>
                  <div style={infoLabelStyle}>Primary Phone Number</div>
                  <div style={infoValueStyle}>
                    {selectedOrder.phone ? (
                      <a
                        href={`tel:${selectedOrder.phone}`}
                        style={{ color: "#063b2a", fontWeight: "bold", textDecoration: "none" }}
                      >
                        📞 {selectedOrder.phone}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>

                {/* ALTERNATE NUMBER */}
                <div
                  style={{
                    ...infoCardStyle,
                    background:
                      selectedOrder.alt_phone || selectedOrder.alternate_phone || selectedOrder.altPhone
                        ? "#f0f9ff"
                        : "#f9f9f9",
                    borderColor:
                      selectedOrder.alt_phone || selectedOrder.alternate_phone || selectedOrder.altPhone
                        ? "#bae6fd"
                        : "#eee",
                  }}
                >
                  <div
                    style={{
                      ...infoLabelStyle,
                      color:
                        selectedOrder.alt_phone || selectedOrder.alternate_phone || selectedOrder.altPhone
                          ? "#0369a1"
                          : "#777",
                    }}
                  >
                    Alternate Phone Number
                  </div>
                  <div style={infoValueStyle}>
                    {selectedOrder.alt_phone || selectedOrder.alternate_phone || selectedOrder.altPhone ? (
                      <a
                        href={`tel:${selectedOrder.alt_phone || selectedOrder.alternate_phone || selectedOrder.altPhone}`}
                        style={{ color: "#0284c7", fontWeight: "bold", textDecoration: "none" }}
                      >
                        📱 {selectedOrder.alt_phone || selectedOrder.alternate_phone || selectedOrder.altPhone}
                      </a>
                    ) : (
                      <span style={{ color: "#9ca3af", fontStyle: "italic", fontSize: 12 }}>
                        Not provided by user
                      </span>
                    )}
                  </div>
                </div>

                <div style={infoCardStyle}>
                  <div style={infoLabelStyle}>Email</div>
                  <div style={{ ...infoValueStyle, wordBreak: "break-all" }}>
                    {selectedOrder.email || "-"}
                  </div>
                </div>

                <div style={infoCardStyle}>
                  <div style={infoLabelStyle}>Payment Method</div>
                  <div style={{ ...infoValueStyle, textTransform: "uppercase" }}>
                    {selectedOrder.payment_method === "razorpay"
                      ? "⚡ Razorpay Online (Instant Paid)"
                      : selectedOrder.payment_method === "cod"
                      ? "Cash On Delivery (COD)"
                      : selectedOrder.payment_method || "Online"}
                  </div>
                </div>

                <div style={{ ...infoCardStyle, gridColumn: "1 / -1" }}>
                  <div style={infoLabelStyle}>Delivery Address</div>
                  <div style={{ ...infoValueStyle, lineHeight: 1.5 }}>
                    {selectedOrder.address}, {selectedOrder.city}, {selectedOrder.state} - {selectedOrder.pin_code}
                  </div>
                </div>
              </div>

              {/* PAYMENT INFORMATION */}
              <h3 style={{ color: "#063b2a", margin: "0 0 12px", fontSize: 16 }}>
                Payment & Billing
              </h3>
              <div
                className="modal-billing-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 12,
                  marginBottom: 24,
                }}
              >
                <div style={infoCardStyle}>
                  <div style={infoLabelStyle}>Total Amount</div>
                  <div style={{ fontSize: 20, fontWeight: "bold", color: "#063b2a" }}>
                    ₹{parseFloat(selectedOrder.total_amount || 0).toLocaleString("en-IN")}
                  </div>
                </div>

                <div style={infoCardStyle}>
                  <div style={infoLabelStyle}>Payment Status</div>
                  <div style={{ marginTop: 4 }}>
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: 20,
                        fontWeight: "bold",
                        fontSize: 11,
                        textTransform: "capitalize",
                        ...getPaymentStatusStyle(selectedOrder.payment_status),
                      }}
                    >
                      {selectedOrder.payment_status || "pending"}
                    </span>
                  </div>
                </div>

                {selectedOrder.shipping_fee && (
                  <div style={infoCardStyle}>
                    <div style={infoLabelStyle}>Shipping Fee</div>
                    <div style={infoValueStyle}>₹{parseFloat(selectedOrder.shipping_fee).toLocaleString("en-IN")}</div>
                  </div>
                )}

                {selectedOrder.discount_amount && (
                  <div style={infoCardStyle}>
                    <div style={infoLabelStyle}>Discount</div>
                    <div style={{ ...infoValueStyle, color: "#198754" }}>
                      -₹{parseFloat(selectedOrder.discount_amount).toLocaleString("en-IN")}
                    </div>
                  </div>
                )}
                {selectedOrder.razorpay_payment_id && (
                  <div style={infoCardStyle}>
                    <div style={infoLabelStyle}>Razorpay Payment ID</div>
                    <div style={{ ...infoValueStyle, fontFamily: "monospace", fontSize: 12 }}>
                      {selectedOrder.razorpay_payment_id}
                    </div>
                  </div>
                )}

                {selectedOrder.razorpay_order_id && (
                  <div style={infoCardStyle}>
                    <div style={infoLabelStyle}>Razorpay Order ID</div>
                    <div style={{ ...infoValueStyle, fontFamily: "monospace", fontSize: 12 }}>
                      {selectedOrder.razorpay_order_id}
                    </div>
                  </div>
                )}
              </div>

              {/* PAYMENT ACTIONS (CONFIRM COD OR APPROVE UPI OR RAZORPAY VERIFIED) */}
              {(!selectedOrder.order_status ||
                selectedOrder.order_status === "pending" ||
                selectedOrder.order_status === "payment_pending") && (
                <div style={{ marginBottom: 24 }}>
                  {selectedOrder.payment_method === "razorpay" ? (
                    <div
                      style={{
                        padding: "12px 18px",
                        background: "#ecfdf5",
                        color: "#065f46",
                        border: "1px solid #a7f3d0",
                        borderRadius: 8,
                        fontWeight: "bold",
                        fontSize: 13,
                        textAlign: "center",
                      }}
                    >
                      ✓ Instant Online Payment Captured via Razorpay ({selectedOrder.razorpay_payment_id || "Verified"})
                    </div>
                  ) : selectedOrder.payment_method === "cod" ? (
                    <button
                      disabled={processingPayment === selectedOrder.id}
                      onClick={() => confirmCODOrder(selectedOrder)}
                      style={{
                        width: "100%",
                        padding: "12px 18px",
                        background: "#0f5132",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        cursor: "pointer",
                        fontWeight: "bold",
                        fontSize: 14,
                      }}
                    >
                      ✓ Confirm COD Order
                    </button>
                  ) : (
                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        disabled={
                          processingPayment === selectedOrder.id ||
                          !getPaymentScreenshot(selectedOrder)
                        }
                        onClick={() => approvePayment(selectedOrder)}
                        style={{
                          flex: 1,
                          padding: "12px 18px",
                          background:
                            processingPayment === selectedOrder.id ||
                            !getPaymentScreenshot(selectedOrder)
                              ? "#aaa"
                              : "#198754",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          cursor:
                            processingPayment === selectedOrder.id ||
                            !getPaymentScreenshot(selectedOrder)
                              ? "not-allowed"
                              : "pointer",
                          fontWeight: "bold",
                          fontSize: 14,
                        }}
                      >
                        ✓ Approve Payment
                      </button>

                      <button
                        disabled={processingPayment === selectedOrder.id}
                        onClick={() => rejectPayment(selectedOrder)}
                        style={{
                          flex: 1,
                          padding: "12px 18px",
                          background:
                            processingPayment === selectedOrder.id ? "#aaa" : "#dc3545",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          cursor: "pointer",
                          fontWeight: "bold",
                          fontSize: 14,
                        }}
                      >
                        ✕ Reject Payment
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ORDER ITEMS TABLE */}
              <h3 style={{ color: "#063b2a", margin: "0 0 12px", fontSize: 16 }}>
                Order Items
              </h3>
              {selectedOrder.items && selectedOrder.items.length > 0 ? (
                <div
                  className="table-scroll-wrapper"
                  style={{
                    border: "1px solid #eee",
                    borderRadius: 10,
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                    marginBottom: 24,
                    width: "100%",
                  }}
                >
                  <table style={{ width: "100%", minWidth: 500, borderCollapse: "collapse" }}>
                    <thead style={{ background: "#063b2a", color: "#fff", fontSize: 12 }}>
                      <tr>
                        <th style={modalTableHeader}>Item</th>
                        <th style={modalTableHeader}>Product Name</th>
                        <th style={modalTableHeader}>Quantity</th>
                        <th style={modalTableHeader}>Price</th>
                        <th style={modalTableHeader}>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items.map((item, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                          <td style={modalTableCell}>
                            {(() => {
                              const itemImg =
                                item.img_url ||
                                item.image ||
                                item.imageUrl ||
                                item.mainImage ||
                                item.img ||
                                (item.variant && (item.variant.mainImage || item.variant.img_url || item.variant.image)) ||
                                (Array.isArray(item.thumbnails) && item.thumbnails[0]) ||
                                (item.variant && Array.isArray(item.variant.thumbnails) && item.variant.thumbnails[0]) ||
                                "";

                              return (
                                <>
                                  {itemImg ? (
                                    <img
                                      src={itemImg}
                                      alt={item.name}
                                      style={{
                                        width: 50,
                                        height: 60,
                                        objectFit: "cover",
                                        borderRadius: 6,
                                        border: "1px solid #ddd",
                                      }}
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                        if (e.currentTarget.nextSibling) {
                                          e.currentTarget.nextSibling.style.display = "flex";
                                        }
                                      }}
                                    />
                                  ) : null}
                                  <div
                                    style={{
                                      width: 50,
                                      height: 60,
                                      display: itemImg ? "none" : "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "#f0f4f2",
                                      border: "1px solid #d0ddd5",
                                      borderRadius: 6,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: "#063b2a",
                                      textAlign: "center",
                                    }}
                                  >
                                    SAREE
                                  </div>
                                </>
                              );
                            })()}
                          </td>
                          <td style={modalTableCell}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                              {(() => {
                                const pid =
                                  item.product_id ||
                                  item.productId ||
                                  (item.id && !String(item.id).startsWith("guest_") ? item.id : "");
                                return pid ? (
                                  <span
                                    style={{
                                      background: "#063b2a",
                                      color: "#fff",
                                      padding: "2px 7px",
                                      borderRadius: 4,
                                      fontSize: 11,
                                      fontWeight: 700,
                                      fontFamily: "monospace",
                                      letterSpacing: 0.5,
                                    }}
                                  >
                                    Product ID: #{pid}
                                  </span>
                                ) : null;
                              })()}
                              {(() => {
                                const colour = item.colour || (item.variant && item.variant.colour) || "";
                                return colour ? (
                                  <span
                                    style={{
                                      background: "#e0f2fe",
                                      color: "#0369a1",
                                      padding: "2px 6px",
                                      borderRadius: 4,
                                      fontSize: 11,
                                      fontWeight: 600,
                                    }}
                                  >
                                    Colour: {colour}
                                  </span>
                                ) : null;
                              })()}
                            </div>
                            <strong style={{ fontSize: 13, color: "#111" }}>{item.name}</strong>
                            {item.variant && item.variant.size && (
                              <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                                Size: {item.variant.size}
                              </div>
                            )}
                          </td>
                          <td style={modalTableCell}>{item.quantity}</td>
                          <td style={modalTableCell}>
                            ₹{parseFloat(item.price || 0).toLocaleString("en-IN")}
                          </td>
                          <td style={{ ...modalTableCell, fontWeight: "bold", color: "#063b2a" }}>
                            ₹
                            {(
                              parseFloat(item.price || 0) * parseFloat(item.quantity || 1)
                            ).toLocaleString("en-IN")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ color: "#777", marginBottom: 20 }}>No items detailed</div>
              )}

              {/* DANGER ZONE: DELETE ORDER */}
              <div
                style={{
                  marginTop: 24,
                  padding: "16px 18px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 8,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#991b1b" }}>
                    Danger Zone: Delete Order
                  </div>
                  <div style={{ fontSize: 12, color: "#7f1d1d", marginTop: 2 }}>
                    Permanently delete this order record with pattern confirmation verification.
                  </div>
                </div>

                <button
                  onClick={() => openDeleteModal(selectedOrder)}
                  style={{
                    padding: "9px 18px",
                    background: "#dc2626",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
                    transition: "background 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#b91c1c";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#dc2626";
                  }}
                >
                  <Trash2 size={15} />
                  Delete Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==============================================================
          HIGHLY CONFIRMING DELETE ORDER MODAL (PATTERN ALERT)
      ============================================================== */}
      {deleteModal.open && deleteModal.order && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.75)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 12000,
            padding: 20,
            animation: "fadeIn 0.15s ease-out",
          }}
          onClick={closeDeleteModal}
        >
          <div
            className="modal-responsive-card"
            style={{
              background: "#ffffff",
              borderRadius: 16,
              width: "100%",
              maxWidth: 520,
              boxShadow: "0 25px 60px -12px rgba(0, 0, 0, 0.35)",
              border: "1.5px solid #fecaca",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ALERT MODAL HEADER */}
            <div
              style={{
                background: "#fef2f2",
                padding: "20px 24px",
                borderBottom: "1px solid #fee2e2",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: "#fee2e2",
                  border: "2px solid #fca5a5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#dc2626",
                  flexShrink: 0,
                }}
              >
                <Trash2 size={22} />
              </div>
              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    margin: "0 0 2px",
                    color: "#991b1b",
                    fontSize: 18,
                    fontWeight: 700,
                  }}
                >
                  Confirm Permanent Deletion
                </h3>
                <div style={{ color: "#b91c1c", fontSize: 12 }}>
                  High-security pattern verification required
                </div>
              </div>
              <button
                disabled={deleteModal.isDeleting}
                onClick={closeDeleteModal}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 20,
                  color: "#9ca3af",
                  cursor: deleteModal.isDeleting ? "not-allowed" : "pointer",
                  padding: 4,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            {/* ALERT BODY */}
            <div style={{ padding: "20px 24px" }}>
              {/* WARNING BOX */}
              <div
                style={{
                  background: "#fff1f2",
                  border: "1px solid #fecdd3",
                  borderRadius: 8,
                  padding: "12px 14px",
                  marginBottom: 16,
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                }}
              >
                <AlertCircle size={18} color="#e11d48" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 12, color: "#9f1239", lineHeight: 1.5 }}>
                  <strong>This action CANNOT be undone.</strong> Deleting this order will permanently
                  remove its line items, customer delivery address, payment records, and courier tracking
                  from the database.
                </div>
              </div>

              {/* TARGET ORDER DETAILS SUMMARY CARD */}
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  padding: "12px 14px",
                  marginBottom: 18,
                  fontSize: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingBottom: 6,
                    borderBottom: "1px solid #f3f4f6",
                    marginBottom: 6,
                  }}
                >
                  <span style={{ color: "#6b7280" }}>Order ID / Code:</span>
                  <strong style={{ color: "#063b2a", fontFamily: "monospace", fontSize: 13 }}>
                    {getOrderCode(deleteModal.order)}
                  </strong>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingBottom: 6,
                    borderBottom: "1px solid #f3f4f6",
                    marginBottom: 6,
                  }}
                >
                  <span style={{ color: "#6b7280" }}>Customer:</span>
                  <strong style={{ color: "#111827" }}>
                    {deleteModal.order.full_name} ({deleteModal.order.phone})
                  </strong>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    paddingBottom: 6,
                    borderBottom: "1px solid #f3f4f6",
                    marginBottom: 6,
                  }}
                >
                  <span style={{ color: "#6b7280" }}>Booked On:</span>
                  <span style={{ color: "#374151" }}>
                    {formatDateTimeDDMMYYYY(deleteModal.order.created_at)}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ color: "#6b7280" }}>Total Amount:</span>
                  <strong style={{ color: "#063b2a", fontSize: 13 }}>
                    ₹{parseFloat(deleteModal.order.total_amount || 0).toLocaleString("en-IN")}
                  </strong>
                </div>
              </div>

              {/* PATTERN INPUT INSTRUCTION */}
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: 6,
                  }}
                >
                  To confirm deletion, please type{" "}
                  <code
                    style={{
                      background: "#fee2e2",
                      color: "#991b1b",
                      padding: "2px 6px",
                      borderRadius: 4,
                      fontWeight: 700,
                      fontFamily: "monospace",
                    }}
                  >
                    {getOrderCode(deleteModal.order)}
                  </code>{" "}
                  or{" "}
                  <code
                    style={{
                      background: "#fee2e2",
                      color: "#991b1b",
                      padding: "2px 6px",
                      borderRadius: 4,
                      fontWeight: 700,
                      fontFamily: "monospace",
                    }}
                  >
                    DELETE
                  </code>{" "}
                  below:
                </label>

                <input
                  type="text"
                  autoFocus
                  disabled={deleteModal.isDeleting}
                  value={deleteModal.confirmInput}
                  onChange={(e) =>
                    setDeleteModal({
                      ...deleteModal,
                      confirmInput: e.target.value,
                      errorMessage: "",
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && isDeletePatternMatched && !deleteModal.isDeleting) {
                      handleConfirmDelete();
                    }
                  }}
                  placeholder={`Type "${getOrderCode(deleteModal.order)}" or "DELETE"`}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: isDeletePatternMatched
                      ? "2px solid #10b981"
                      : deleteModal.confirmInput.trim()
                      ? "2px solid #f87171"
                      : "1.5px solid #d1d5db",
                    background: isDeletePatternMatched ? "#f0fdf4" : "#ffffff",
                    fontSize: 13,
                    outline: "none",
                    fontFamily: "monospace",
                    fontWeight: 600,
                    letterSpacing: 0.5,
                  }}
                />

                {/* REAL-TIME PATTERN VALIDATION FEEDBACK */}
                <div style={{ marginTop: 6, fontSize: 11 }}>
                  {!deleteModal.confirmInput.trim() ? (
                    <span style={{ color: "#6b7280" }}>
                      ⚠️ Enter the confirmation pattern above to unlock deletion.
                    </span>
                  ) : isDeletePatternMatched ? (
                    <span style={{ color: "#059669", fontWeight: 600 }}>
                      ✓ Pattern verified! Permanent deletion is unlocked.
                    </span>
                  ) : (
                    <span style={{ color: "#dc2626", fontWeight: 500 }}>
                      ✗ Pattern does not match yet. (Type {getOrderCode(deleteModal.order)} or DELETE)
                    </span>
                  )}
                </div>
              </div>

              {/* ERROR MESSAGE DISPLAY IF ANY */}
              {deleteModal.errorMessage && (
                <div
                  style={{
                    background: "#fee2e2",
                    border: "1px solid #fca5a5",
                    color: "#991b1b",
                    padding: "8px 12px",
                    borderRadius: 6,
                    fontSize: 12,
                    marginBottom: 16,
                  }}
                >
                  {deleteModal.errorMessage}
                </div>
              )}

              {/* ACTIONS */}
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button
                  type="button"
                  disabled={deleteModal.isDeleting}
                  onClick={closeDeleteModal}
                  style={{
                    flex: 1,
                    padding: "10px 16px",
                    background: "#f3f4f6",
                    color: "#374151",
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    cursor: deleteModal.isDeleting ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={!isDeletePatternMatched || deleteModal.isDeleting}
                  onClick={handleConfirmDelete}
                  style={{
                    flex: 2,
                    padding: "10px 16px",
                    background:
                      !isDeletePatternMatched || deleteModal.isDeleting ? "#fca5a5" : "#dc2626",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    cursor:
                      !isDeletePatternMatched || deleteModal.isDeleting ? "not-allowed" : "pointer",
                    fontWeight: 700,
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    boxShadow:
                      isDeletePatternMatched && !deleteModal.isDeleting
                        ? "0 4px 12px rgba(220, 38, 38, 0.3)"
                        : "none",
                    transition: "all 0.15s ease",
                  }}
                >
                  {deleteModal.isDeleting ? (
                    <>
                      <RefreshCw size={14} className="spin-icon" />
                      <span>Deleting Order...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 size={15} />
                      <span>Permanently Delete Order</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Keyframes & Responsive Media Queries */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin-icon {
          animation: spin 1s linear infinite;
        }

        /* Responsive Mobile Optimizations for Admin Orders */
        @media (max-width: 768px) {
          .orders-container {
            padding: 12px 10px !important;
          }
          .orders-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
          }
          .orders-header h1 {
            font-size: 20px !important;
          }
          .orders-refresh-btn {
            width: 100% !important;
            justify-content: center !important;
          }
          .orders-stats-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 8px !important;
            margin-bottom: 14px !important;
          }
          .orders-stat-card {
            padding: 8px 6px !important;
            border-radius: 8px !important;
          }
          .orders-stat-title {
            font-size: 9px !important;
            letter-spacing: 0px !important;
          }
          .orders-stat-value {
            font-size: 17px !important;
            margin-top: 2px !important;
          }
          .orders-stat-sub {
            display: none !important;
          }
          .orders-filter-box {
            padding: 12px !important;
            margin-bottom: 16px !important;
          }
          .orders-filter-top {
            flex-direction: column !important;
            gap: 8px !important;
          }
          .orders-search-box {
            flex: 1 1 100% !important;
            width: 100% !important;
          }
          .orders-status-box {
            flex: 1 1 100% !important;
            width: 100% !important;
          }
          .orders-reset-btn {
            width: 100% !important;
            justify-content: center !important;
          }
          .orders-date-row {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
          }
          .orders-date-presets {
            overflow-x: auto !important;
            white-space: nowrap !important;
            -webkit-overflow-scrolling: touch !important;
            width: 100% !important;
            padding-bottom: 4px !important;
            gap: 6px !important;
          }
          .orders-date-presets button {
            flex-shrink: 0 !important;
            padding: 5px 10px !important;
            font-size: 11px !important;
          }
          .orders-custom-dates {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            width: 100% !important;
            gap: 8px !important;
          }
          .orders-custom-dates > div {
            width: 100% !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .orders-custom-dates input {
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .orders-desktop-table-wrap {
            display: none !important;
          }
          .orders-mobile-cards-list {
            display: flex !important;
            flex-direction: column !important;
            gap: 12px !important;
          }
          .modal-responsive-card {
            max-width: 95vw !important;
            padding: 16px !important;
            box-sizing: border-box !important;
          }
          .order-details-modal-content {
            max-width: 96vw !important;
            max-height: 94vh !important;
            box-sizing: border-box !important;
          }
          .order-details-modal-header {
            padding: 14px 16px !important;
          }
          .order-details-modal-body {
            padding: 14px 12px !important;
          }
          .modal-customer-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 8px !important;
          }
          .modal-billing-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 8px !important;
          }
        }
        @media (max-width: 520px) {
          .orders-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 6px !important;
          }
          .modal-customer-grid {
            grid-template-columns: 1fr !important;
            gap: 6px !important;
          }
          .modal-billing-grid {
            grid-template-columns: 1fr !important;
            gap: 6px !important;
          }
          .orders-card-actions-row {
            flex-wrap: wrap !important;
          }
        }
        @media (min-width: 769px) {
          .orders-mobile-cards-list {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

// ============================
// STYLES
// ============================
const thStyle = {
  padding: "12px 10px",
  textAlign: "left",
  whiteSpace: "nowrap",
  fontSize: 12,
  fontWeight: 600,
};

const tdStyle = {
  padding: "12px 10px",
  verticalAlign: "middle",
};

const statCardStyle = {
  background: "#fff",
  borderRadius: 10,
  padding: "14px 16px",
  border: "1.5px solid #e0e0e0",
  boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
  cursor: "pointer",
  transition: "all 0.2s ease",
};

const statCardTitle = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.5,
  color: "#555",
};

const modalTableHeader = {
  padding: "10px",
  textAlign: "left",
  fontSize: 12,
};

const modalTableCell = {
  padding: "10px",
  verticalAlign: "middle",
};

const infoCardStyle = {
  background: "#f9f9f9",
  border: "1px solid #eee",
  borderRadius: 8,
  padding: "10px 12px",
};

const infoLabelStyle = {
  fontSize: 11,
  color: "#777",
  fontWeight: 600,
  textTransform: "uppercase",
  marginBottom: 4,
};

const infoValueStyle = {
  fontSize: 13,
  color: "#222",
  fontWeight: 500,
};

const modalInputLabel = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "#444",
  marginBottom: 4,
};

const modalInputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "9px 12px",
  borderRadius: 6,
  border: "1.5px solid #ccc",
  fontSize: 13,
  outline: "none",
};

export default AdminOrders;
